import React, { useState } from 'react';
import { FaCalendarAlt, FaMapMarkerAlt, FaUser, FaTag, FaClock, FaTicketAlt, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaUsers, FaChartBar, FaSpinner } from 'react-icons/fa';
import ConfirmationModal from '../../../../../components/ConfirmationModal';
import { descargarInformeEventoEspecifico } from '../../../../../components/http/Actions/EstadisticasAction';

const EventoCards = ({ eventos, onManageSecciones, onManageTickets, onEditEvento, onToggleStatus, onDeleteEvento }) => {
  // Estados existentes para el modal de confirmación
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'danger',
    title: '',
    message: '',
    confirmText: '',
    onConfirm: null,
    evento: null
  });
  const [isProcessing, setIsProcessing] = useState(false);
  
  // NUEVO: Estado para descargas de informes
  const [downloadingStates, setDownloadingStates] = useState({});

  // Funciones existentes (formatDate, formatDuration, isEventoPasado, isColorDark)...
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('es-CL', options);
  };

  const formatDuration = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  const isEventoPasado = (fecha) => {
    return new Date(fecha) < new Date();
  };

  const isColorDark = (hexColor) => {
    if (!hexColor || !hexColor.startsWith('#')) return false;
    
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  // Función para descargar informe del evento específico
  const handleDescargarInformeEvento = async (eventoId, titulo) => {
    try {
      setDownloadingStates(prev => ({ ...prev, [eventoId]: true }));
      const token = localStorage.getItem('token');
      await descargarInformeEventoEspecifico(eventoId, token);
    } catch (error) {
      console.error('Error al descargar informe del evento:', error);
      alert(`Error al descargar el informe del evento: ${error}`);
    } finally {
      setDownloadingStates(prev => ({ ...prev, [eventoId]: false }));
    }
  };

  const handleToggleStatus = (evento) => {
    const isDisabling = evento.habilitado;
    setConfirmModal({
      isOpen: true,
      type: isDisabling ? 'warning' : 'info',
      title: isDisabling ? 'Deshabilitar Evento' : 'Habilitar Evento',
      message: isDisabling 
        ? `¿Estás seguro de que quieres deshabilitar el evento "${evento.titulo}"? Los usuarios no podrán verlo en la lista pública.`
        : `¿Estás seguro de que quieres habilitar el evento "${evento.titulo}"? Los usuarios podrán verlo en la lista pública.`,
      confirmText: isDisabling ? 'Deshabilitar' : 'Habilitar',
      onConfirm: () => confirmToggleStatus(evento.eventoId),
      evento: evento
    });
  };

 const handleDeleteEvento = (evento) => {
   setConfirmModal({
     isOpen: true,
     type: 'danger',
     title: 'Eliminar Evento',
     message: `¿Estás seguro de que quieres eliminar el evento "${evento.titulo}"? Esta acción no se puede deshacer y se eliminarán todas las secciones asociadas (si no tienen tickets vendidos).`,
     confirmText: 'Eliminar',
     onConfirm: () => confirmDeleteEvento(evento.eventoId),
     evento: evento
   });
 };

 const confirmToggleStatus = async (eventoId) => {
   setIsProcessing(true);
   try {
     await onToggleStatus(eventoId);
     closeConfirmModal();
   } catch (error) {
     console.error('Error toggling status:', error);
   } finally {
     setIsProcessing(false);
   }
 };

 const confirmDeleteEvento = async (eventoId) => {
   setIsProcessing(true);
   try {
     await onDeleteEvento(eventoId);
     closeConfirmModal();
   } catch (error) {
     console.error('Error deleting evento:', error);
   } finally {
     setIsProcessing(false);
   }
 };

 const closeConfirmModal = () => {
   setConfirmModal({
     isOpen: false,
     type: 'danger',
     title: '',
     message: '',
     confirmText: '',
     onConfirm: null,
     evento: null
   });
 };

 return (
   <>
     <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
       {eventos.length > 0 ? (
         eventos.map((evento) => {
           const isPasado = isEventoPasado(evento.fecha);
           const comediantePrincipal = evento.comediantes?.find(c => c.esPrincipal) || evento.comediantes?.[0];
           
           return (
             <div 
               key={evento.eventoId} 
               className={`bg-zinc-800 rounded-xl shadow-lg border border-zinc-700 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:border-zinc-600 hover:scale-[1.02] ${
                 isPasado ? 'opacity-70' : ''
               }`}
             >
               {/* Imagen del evento */}
               <div className="relative h-48 bg-zinc-700 overflow-hidden">
                 <img 
                   src={evento.imagen} 
                   alt={evento.titulo} 
                   className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                   onError={(e) => {
                     e.target.onerror = null;
                     e.target.src = 'https://via.placeholder.com/400x200/4a5568/cbd5e0?text=Sin+Imagen';
                   }}
                 />
                 
                 {/* Overlay con duración */}
                 <div className="absolute bottom-3 left-3">
                   <span className="bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs flex items-center">
                     <FaClock className="mr-1" />
                     {formatDuration(evento.duracionMinutos)}
                   </span>
                 </div>

                 {/* Badge de estado */}
                 <div className="absolute top-3 right-3">
                   <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${
                     evento.habilitado 
                       ? isPasado 
                         ? 'bg-gray-800/80 text-gray-200 border border-gray-600' 
                         : 'bg-green-800/80 text-green-200 border border-green-600'
                       : 'bg-red-800/80 text-red-200 border border-red-600'
                   }`}>
                     {evento.habilitado 
                       ? isPasado 
                         ? 'Finalizado' 
                         : 'Activo'
                       : 'Deshabilitado'}
                   </span>
                 </div>
               </div>

               {/* Contenido de la tarjeta */}
               <div className="p-6">
                 {/* Título */}
                 <div className="mb-4">
                   <h3 className="text-xl font-bold text-white mb-2 line-clamp-2 hover:text-red-400 transition-colors">
                     {evento.titulo}
                   </h3>
                   {evento.descripcion && (
                     <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed">
                       {evento.descripcion}
                     </p>
                   )}
                 </div>

                 {/* Fecha y lugar */}
                 <div className="mb-4 space-y-2">
                   <div className="flex items-center text-sm text-white">
                     <div className="w-8 h-8 bg-red-600/20 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                       <FaCalendarAlt className="text-red-500 text-xs" />
                     </div>
                     <div>
                       <div className="font-medium">{formatDate(evento.fecha)}</div>
                       {isPasado && (
                         <div className="text-xs text-gray-500">Evento pasado</div>
                       )}
                     </div>
                   </div>
                   
                   {evento.lugar && (
                     <div className="flex items-center text-sm text-gray-300">
                       <div className="w-8 h-8 bg-red-600/20 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                         <FaMapMarkerAlt className="text-red-500 text-xs" />
                       </div>
                       <div>
                         <div className="font-medium text-white">{evento.lugar.nombre}</div>
                         <div className="text-xs text-gray-400">{evento.lugar.ciudad}, {evento.lugar.region}</div>
                       </div>
                     </div>
                   )}
                 </div>

                 {/* Comediante principal */}
                 {comediantePrincipal && (
                   <div className="mb-4">
                     <div className="flex items-center text-sm">
                       <div className="w-8 h-8 bg-yellow-600/20 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                         <FaUser className="text-yellow-500 text-xs" />
                       </div>
                       <div>
                         <div className="text-white font-medium">{comediantePrincipal.nombre}</div>
                         <div className="flex items-center gap-2">
                           {comediantePrincipal.esPrincipal && (
                             <span className="px-2 py-0.5 bg-yellow-600 text-yellow-100 text-xs rounded-full">
                               Principal
                             </span>
                           )}
                           {evento.comediantes && evento.comediantes.length > 1 && (
                             <span className="text-xs text-gray-400">
                               +{evento.comediantes.length - 1} más
                             </span>
                           )}
                         </div>
                       </div>
                     </div>
                   </div>
                 )}

                 {/* Tags */}
                 {evento.tags && evento.tags.length > 0 && (
                   <div className="mb-6">
                     <div className="flex flex-wrap gap-2">
                       {evento.tags.slice(0, 3).map((tag) => {
                         const isDark = isColorDark(tag.colorHex);
                         return (
                           <span 
                             key={tag.tagId} 
                             className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105" 
                             style={{ 
                               backgroundColor: `${tag.colorHex}25`, 
                               color: tag.colorHex,
                               border: `1px solid ${tag.colorHex}50`,
                               textShadow: isDark ? '0 0 10px rgba(0,0,0,0.8)' : 'none',
                             }}
                           >
                             <FaTag className="mr-1 text-xs" />
                             {tag.nombre}
                           </span>
                         );
                       })}
                       {evento.tags.length > 3 && (
                         <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-zinc-700 text-gray-300 border border-zinc-600">
                           +{evento.tags.length - 3} más
                         </span>
                       )}
                     </div>
                   </div>
                 )}

                 {/* Espaciador flexible */}
                 <div className="flex-1"></div>

                 {/* Botones de acción */}
                 <div className="pt-4 border-t border-zinc-700 space-y-3">
                   {/* Botón principal: Gestionar Secciones */}
                   <button
                     onClick={() => onManageSecciones(evento)}
                     className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center transform hover:scale-105 hover:shadow-lg"
                     title="Gestionar Secciones"
                   >
                     <FaTicketAlt className="mr-2" />
                     Gestionar Secciones
                   </button>

                   {/* Botón para gestionar tickets */}
                   <button
                     onClick={() => onManageTickets(evento)}
                     className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center transform hover:scale-105 hover:shadow-lg"
                     title="Gestionar Tickets"
                   >
                     <FaUsers className="mr-2" />
                     Gestionar Tickets
                   </button>

                   <button
                     onClick={() => handleDescargarInformeEvento(evento.eventoId, evento.titulo)}
                     disabled={downloadingStates[evento.eventoId]}
                     className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                     title="Descargar Informe del Evento"
                   >
                     {downloadingStates[evento.eventoId] ? (
                       <>
                         <FaSpinner className="mr-2 animate-spin" />
                         Generando Informe...
                       </>
                     ) : (
                       <>
                         <FaChartBar className="mr-2" />
                         Descargar Informe
                       </>
                     )}
                   </button>

                   {/* Botones secundarios */}
                   <div className="grid grid-cols-3 gap-2">
                     {/* Botón editar */}
                     <button
                       onClick={() => onEditEvento(evento)}
                       className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center"
                       title="Editar Evento"
                     >
                       <FaEdit className="text-sm" />
                     </button>
                     
                     {/* Botón toggle status */}
                     <button
                       onClick={() => handleToggleStatus(evento)}
                       className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center ${
                         evento.habilitado 
                           ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                           : 'bg-green-600 hover:bg-green-700 text-white'
                       }`}
                       title={evento.habilitado ? 'Deshabilitar' : 'Habilitar'}
                     >
                       {evento.habilitado ? <FaToggleOff className="text-sm" /> : <FaToggleOn className="text-sm" />}
                     </button>
                     
                     {/* Botón eliminar */}
                     <button
                       onClick={() => handleDeleteEvento(evento)}
                       className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center"
                       title="Eliminar Evento"
                     >
                       <FaTrash className="text-sm" />
                     </button>
                   </div>
                 </div>
               </div>
             </div>
           );
         })
       ) : (
         <div className="col-span-full flex flex-col items-center justify-center py-16 text-gray-400">
           <div className="w-24 h-24 bg-zinc-800 rounded-full flex items-center justify-center mb-6 border-2 border-zinc-700">
             <FaTicketAlt className="text-4xl opacity-50" />
           </div>
           <h3 className="text-xl font-medium mb-2">No se encontraron eventos</h3>
           <p className="text-center max-w-md">
             No hay eventos que coincidan con tu búsqueda. Intenta ajustar los filtros o crear un nuevo evento.
           </p>
         </div>
       )}
     </div>

     {/* Modal de confirmación */}
     <ConfirmationModal 
       isOpen={confirmModal.isOpen}
       onClose={closeConfirmModal}
       onConfirm={confirmModal.onConfirm}
       title={confirmModal.title}
       message={confirmModal.message}
       confirmText={confirmModal.confirmText}
       type={confirmModal.type}
       isLoading={isProcessing}
     />
   </>
 );
};

export default EventoCards;